import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import { Home, Store, Sparkles, Layers, Briefcase, LifeBuoy, Search, ShoppingBag, Instagram, Package, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useApp } from '@/state/AppContext';
import { classNames } from '@/lib/format';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_ed722c14-f6d3-48d4-b5ee-3466358d0c74/artifacts/mwshbcey_BIG%20BASH%20LOGO.svg';
const COLLAPSED_KEY = 'bb_sidenav_collapsed_v1';

const NAV = [
  { to: '/', label: 'Home', icon: Home, test: 'sidenav-home', exact: true },
  { to: '/shop', label: 'Shop', icon: Store, test: 'sidenav-shop' },
  { to: '/customize', label: 'Customize', icon: Sparkles, test: 'sidenav-customize', highlight: true },
  { to: '/collections', label: 'Collections', icon: Layers, test: 'sidenav-collections', badge: 'Soon' },
  { to: '/bulk-orders', label: 'Bulk Orders', icon: Briefcase, test: 'sidenav-bulk' },
  { to: '/support', label: 'Support', icon: LifeBuoy, test: 'sidenav-support' },
  { to: '/profile', label: 'Track Order', icon: Package, test: 'sidenav-profile' },
];

export const SideNav = () => {
  const { cartCount, setSearchOpen, config } = useApp();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  // Cart-icon bump animation when cart count increases
  const prevCountRef = useRef(cartCount);
  const [bumpKey, setBumpKey] = useState(0);
  useEffect(() => {
    if (cartCount > prevCountRef.current) setBumpKey((k) => k + 1);
    prevCountRef.current = cartCount;
  }, [cartCount]);
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem(COLLAPSED_KEY);
    if (stored !== null) return stored === '1';
    // Auto-collapse on narrow desktops
    return window.matchMedia('(max-width: 1100px)').matches;
  });

  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0'); } catch {}
    document.documentElement.style.setProperty('--bb-sidenav-w', collapsed ? '64px' : '240px');
  }, [collapsed]);

  // Sync to css variable on mount
  useEffect(() => {
    document.documentElement.style.setProperty('--bb-sidenav-w', collapsed ? '64px' : '240px');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = (item) => {
    if (item.exact) return pathname === item.to;
    const [base, query] = item.to.split('?');
    if (query) {
      const want = new URLSearchParams(query);
      const have = new URLSearchParams(search);
      for (const [k, v] of want.entries()) {
        if (have.get(k) !== v) return false;
      }
      return pathname === base || pathname.startsWith(base + '/');
    }
    return pathname === base || pathname.startsWith(base + '/');
  };

  return (
    <aside
      className={classNames(
        'hidden md:flex md:flex-col fixed inset-y-0 left-0 z-40 bg-white border-r-2 border-[var(--bb-darkblue)]/15 transition-[width] duration-200',
        collapsed ? 'w-16' : 'w-60',
      )}
      data-testid="side-nav"
      data-collapsed={collapsed ? 'true' : 'false'}
    >
      <Link
        to="/"
        className={classNames('flex items-center gap-2 border-b-2 border-[var(--bb-darkblue)]/15', collapsed ? 'px-2 py-4 justify-center' : 'px-5 py-5')}
        data-testid="sidenav-logo"
        title="Big Bash"
      >
        <img src={LOGO_URL} alt="Big Bash" className={collapsed ? 'h-8 w-auto' : 'h-10 w-auto'} />
      </Link>

      {/* Collapse toggle */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="absolute top-7 -right-3 w-6 h-6 rounded-full bg-[var(--bb-cyan)] hover:bg-[var(--bb-cyan-deep)] text-white shadow-sm flex items-center justify-center"
        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        data-testid="sidenav-toggle"
      >
        {collapsed ? <ChevronsRight className="w-3.5 h-3.5" /> : <ChevronsLeft className="w-3.5 h-3.5" />}
      </button>

      <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-1">
        {NAV.map((item) => {
          const active = isActive(item);
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              title={collapsed ? item.label : undefined}
              className={classNames(
                'group flex items-center rounded-xl font-nunito-bold text-sm transition-colors',
                collapsed ? 'justify-center w-12 h-12 mx-auto' : 'gap-3 px-3 py-2.5',
                active
                  ? 'bg-[var(--bb-cyan)] text-white'
                  : 'text-[var(--bb-darkblue)] hover:bg-[var(--bb-cyan-soft)]',
                item.highlight && !active && 'bg-[var(--bb-yellow)]/30 text-[var(--bb-darkblue)] hover:bg-[var(--bb-yellow)]/50',
              )}
              data-testid={item.test}
            >
              <Icon className="w-5 h-5 shrink-0" />
              {!collapsed && <span className="flex-1">{item.label}</span>}
              {!collapsed && item.badge && (
                <span className="text-[9px] uppercase tracking-wider font-extrabold px-1.5 py-0.5 rounded-full bg-[var(--bb-darkblue)] text-white">{item.badge}</span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className={classNames('border-t-2 border-[var(--bb-darkblue)]/15 space-y-2', collapsed ? 'px-2 py-3' : 'px-3 py-3')}>
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          title={collapsed ? 'Search' : undefined}
          className={classNames(
            'w-full flex items-center rounded-xl bg-[var(--bb-yellow-soft)] hover:bg-[var(--bb-yellow)] font-nunito-bold text-sm text-[var(--bb-darkblue)] transition-colors',
            collapsed ? 'h-11 justify-center' : 'gap-2 px-3 py-2.5',
          )}
          data-testid="sidenav-search"
        >
          <Search className="w-4 h-4" />
          {!collapsed && <><span className="flex-1 text-left">Search</span>
          <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-white/80 text-[var(--bb-darkblue)] font-nunito-bold">/</kbd></>}
        </button>
        <button
          type="button"
          onClick={() => navigate('/cart')}
          title={collapsed ? 'Cart' : undefined}
          className={classNames(
            'w-full flex items-center rounded-xl bg-[var(--bb-pink)] text-white font-nunito-bold text-sm hover:bg-[var(--bb-pink-deep)] transition-colors relative bb-press-pop',
            collapsed ? 'h-11 justify-center' : 'gap-2 px-3 py-2.5',
          )}
          data-testid="sidenav-cart"
        >
          <ShoppingBag
            key={`sn-cart-${bumpKey}`}
            className={`w-4 h-4 ${bumpKey > 0 ? 'bb-anim-cart-bump' : ''}`}
          />
          {!collapsed && <span className="flex-1 text-left">My Cart</span>}
          {cartCount > 0 && (
            <span
              key={`sn-badge-${bumpKey}`}
              className={classNames(
                'min-w-5 h-5 px-1 rounded-full bg-white text-[var(--bb-pink-deep)] text-[11px] font-extrabold flex items-center justify-center bb-anim-bounce-in',
                collapsed && 'absolute -top-1 -right-1',
              )}
              data-testid="sidenav-cart-count"
            >
              {cartCount}
            </span>
          )}
        </button>
        {!collapsed && (
          <a
            href={`https://www.instagram.com/${config.instagram_handle || 'bigbashconfectioneries'}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-1.5 text-xs font-nunito-bold text-[var(--bb-darkblue)] hover:text-[var(--bb-cyan)] py-1"
            data-testid="sidenav-instagram"
          >
            <Instagram className="w-4 h-4" /> Follow @{config.instagram_handle || 'bigbashconfectioneries'}
          </a>
        )}
      </div>
    </aside>
  );
};
